import * as paudData from './schools/paud';
import * as sdData from './schools/sd';
import * as smpData from './schools/smp';
import * as smkData from './schools/smk';
import type { Student, Staff } from './types';

export type SchoolUnit = 'paud' | 'paud-pekanbaru' | 'paud-kampar' | 'paud-siak' | 'sd' | 'sd-pekanbaru' | 'sd-kampar' | 'smp' | 'smk';
export const validUnits: SchoolUnit[] = ['paud', 'paud-pekanbaru', 'paud-kampar', 'paud-siak', 'sd', 'sd-pekanbaru', 'sd-kampar', 'smp', 'smk'];

/**
 * Get unit-specific data based on school slug
 */
export function getSchoolData(unit: SchoolUnit) {
    if (unit.startsWith('paud')) {
        return paudData;
    }
    if (unit.startsWith('sd')) {
        return sdData;
    }
    switch (unit) {
        case 'smp':
            return smpData;
        case 'smk':
            return smkData;
        default:
            return paudData;
    }
}

/**
 * Get unit-specific status for the health card
 */
export function getUnitStatus(unit: string) {
    const statuses: Record<string, { title: string; desc: string; color: string; icon: any }> = {
        paud: {
            title: 'Unit Sehat',
            desc: 'Seluruh sinkronisasi data antar unit PAUD berjalan lancar. Belum ada temuan audit kritis.',
            color: 'from-primary to-primary-dark',
            icon: 'check'
        },
        sd: {
            title: 'Audit Internal',
            desc: 'Unit SD sedang dalam tahap audit internal rutin. Beberapa data saldo mungkin masih dalam tahap rekonsiliasi.',
            color: 'from-blue-600 to-blue-800',
            icon: 'info'
        },
        smp: {
            title: 'Optimal',
            desc: 'Efisiensi pengelolaan anggaran mencapai 95%. Tidak ada kendala penagihan piutang SPP bulan ini.',
            color: 'from-indigo-600 to-indigo-800',
            icon: 'check'
        },
        smk: {
            title: 'Perhatian Khusus',
            desc: 'Terdapat keterlambatan sinkronisasi data setoran kas selama 2 hari terakhir. Perlu pengecekan manual.',
            color: 'from-orange-500 to-orange-700',
            icon: 'alert'
        },
    };

    return statuses[unit] || statuses.paud;
}

/**
 * Get school name from slug
 */
export function getSchoolName(unit: SchoolUnit): string {
    switch (unit) {
        case 'paud':
            return 'PAUD / TK Unit';
        case 'paud-pekanbaru':
            return 'PAUD Al-Azhar Pekanbaru';
        case 'paud-kampar':
            return 'PAUD Al-Azhar Kampar';
        case 'paud-siak':
            return 'PAUD Al-Azhar Siak';
        case 'sd':
            return 'SD Dasar Unit';
        case 'sd-pekanbaru':
            return 'SD Al-Azhar Pekanbaru';
        case 'sd-kampar':
            return 'SD Al-Azhar Kampar';
        case 'smp':
            return 'SMP Menengah Unit';
        case 'smk':
            return 'SMK Kejuruan Unit';
        default:
            return 'School Unit';
    }
}

/**
 * Get student by ID and unit
 */
export function getStudentData(unit: SchoolUnit, id: string): Student | undefined {
    const schoolData = getSchoolData(unit);
    const students = ((schoolData as any)[`${unit.split('-')[0]}Students`] || []) as Student[];
    return students.find(s => s.id === id);
}

/**
 * Get staff by ID and unit
 */
export function getStaffByNameOrId(unit: SchoolUnit, id: string): Staff | undefined {
    const schoolData = getSchoolData(unit);
    const staff = ((schoolData as any)[`${unit.split('-')[0]}Staff`] || []) as Staff[];
    return staff.find(s => s.id === id);
}

/**
 * Get unit-specific financial statistics
 */
export function getUnitFinancialStats(unit: SchoolUnit) {
    const schoolData = getSchoolData(unit);
    const baseType = unit.split('-')[0];

    if (unit.includes('-')) {
        const schools = (schoolData as any)[`${baseType}Schools`] || [];
        const specificSchool = schools.find((s: any) => s.id === unit);
        if (specificSchool) {
            return {
                income: `Rp ${specificSchool.income.replace(' Jt', '.000.000')}`,
                expense: `Rp ${specificSchool.expense.replace(' Jt', '.000.000')}`,
                balance: `Rp ${specificSchool.balance.replace(' Jt', '.000.000')}`,
                receivable: `Rp ${specificSchool.receivable.replace(' Jt', '.000.000')}`,
            };
        }
    }

    const stats: any[] = (schoolData as any)[`${baseType}Stats`] || [];
    return {
        income: stats.find(s => s.label.toLowerCase().includes('pendapatan'))?.value || 'Rp 0',
        expense: stats.find(s => s.label.toLowerCase().includes('pengeluaran'))?.value || 'Rp 0',
        balance: stats.find(s => s.label.toLowerCase().includes('saldo'))?.value || 'Rp 0',
        receivable: stats.find(s => s.label.toLowerCase().includes('piutang'))?.value || 'Rp 0',
    };
}

/**
 * Get unit-specific journal entries
 */
export function getUnitJournal(unit: SchoolUnit) {
    const schoolData = getSchoolData(unit);
    const baseType = unit.split('-')[0];
    return ((schoolData as any)[`${baseType}JournalEntries`] || []) as any[];
}

/**
 * Get unit-specific chart data (last 12 months)
 */
export function getUnitChartData(unit: SchoolUnit) {
    const schoolData = getSchoolData(unit);
    const baseType = unit.split('-')[0];

    // Try to find specific data key e.g. paudPekanbaruChartData
    const camelUnit = unit.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
    const specificDataKey = `${camelUnit}ChartData`;

    if ((schoolData as any)[specificDataKey]) {
        return (schoolData as any)[specificDataKey] as any[];
    }

    return ((schoolData as any)[`${baseType}ChartData`] || []) as any[];
}

import { NeracaData, UnitPerformance, StaffPerformance, StudentPerformance, PerformanceKPI } from './types';
import { Users, GraduationCap, Clock, Award, CheckCircle2, TrendingUp, TrendingDown } from 'lucide-react';

export function getUnitNeraca(unit: SchoolUnit): NeracaData {
    const stats = getUnitFinancialStats(unit);
    const baseType = unit.split('-')[0];
    const unitName = getSchoolName(unit).replace('AL-AZHAR ', '');

    const parseCurrency = (val: string) => parseInt(val.replace(/[^0-9]/g, '')) || 0;
    const formatCurrency = (val: number) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(val);

    // Base template for Neraca
    const data: NeracaData = {
        assets: {
            current: [
                { name: `Kas/Tunai ${unitName}`, amount: stats.balance },
                { name: 'Bank BCA Operasional', amount: formatCurrency(parseCurrency(stats.balance) * 1.5) },
                { name: 'Piutang SPP Siswa', amount: stats.receivable },
            ],
            nonCurrent: [
                { name: 'Peralatan & Inventaris Kantor', amount: 'Rp 25.500.000' },
                { name: 'Gedung Bangunan Unit', amount: 'Rp 450.000.000' },
            ]
        },
        liabilities: {
            shortTerm: [
                { name: 'Hutang Biaya Operasional', amount: 'Rp 2.500.000' },
            ],
            longTerm: [
                { name: 'Hutang Investasi Sarpras', amount: 'Rp 15.000.000' },
            ]
        },
        equity: {
            base: [
                { name: 'Modal Awal Yayasan', amount: 'Rp 500.000.000' },
                { name: 'Saldo Laba/Defisit Ditahan', amount: formatCurrency(parseCurrency(stats.balance) * 0.8) },
            ]
        }
    };

    // Specific cases for different school levels
    if (baseType === 'sd') {
        data.assets.current.push({ name: 'Persediaan Seragam & Atk', amount: 'Rp 12.000.000' });
        data.liabilities.shortTerm.push({ name: 'Uang Muka Kegiatan SD', amount: 'Rp 5.500.000' });
    } else if (baseType === 'smp') {
        data.assets.nonCurrent.push({ name: 'Aset Lab Komputer & Sains', amount: 'Rp 85.000.000' });
        data.liabilities.shortTerm.push({ name: 'Hutang Pihak Ketiga (Kantin)', amount: 'Rp 1.500.000' });
    } else if (baseType === 'smk') {
        data.assets.nonCurrent.push({ name: 'Aset Bengkel & Workshop', amount: 'Rp 150.000.000' });
        data.assets.nonCurrent.push({ name: 'Aset Kendaraan Praktik', amount: 'Rp 250.000.000' });
        data.liabilities.longTerm[0].amount = 'Rp 75.000.000'; // Higher liability for high-cost unit
    }

    // List of units that should stay BALANCED
    const balancedUnits = [
        'paud-pekanbaru', 'paud',
        'sd-pekanbaru', 'sd',
        'smp-pekanbaru', 'smp',
        'smk-pekanbaru', 'smk'
    ];

    const isTargetBalanced = balancedUnits.includes(unit);

    // Apply variance based on unit name/location
    if (unit.includes('kampar')) {
        // CASE: Unbalanced - missing petty cash record
        data.assets.current[0].amount = formatCurrency(parseCurrency(data.assets.current[0].amount) - 250000);
    } else if (unit.includes('siak')) {
        // CASE: Unbalanced - unrecorded liability
        data.liabilities.shortTerm.push({ name: 'Hutang Vendor Konsumsi', amount: 'Rp 4.500.000' });
    } else if (unit.includes('sorek')) {
        // CASE: Unbalanced - discrepancy in fixed asset valuation
        data.assets.nonCurrent[1].amount = 'Rp 440.000.000';
    }

    // Secondary sub-units (e.g. sd-kampar, smp-siak) should generally be UNBALANCED for variety
    if (!isTargetBalanced && !unit.includes('kampar') && !unit.includes('siak') && !unit.includes('sorek')) {
        // Generic unbalance for other sub-units
        data.assets.current[1].amount = formatCurrency(parseCurrency(data.assets.current[1].amount) + 750000);
    }

    // Final check for Balanced Units: Force them to match if they are in the balanced list
    if (isTargetBalanced) {
        const totalA = data.assets.current.reduce((acc, i) => acc + parseCurrency(i.amount), 0) +
            data.assets.nonCurrent.reduce((acc, i) => acc + parseCurrency(i.amount), 0);
        const totalLP = data.liabilities.shortTerm.reduce((acc, i) => acc + parseCurrency(i.amount), 0) +
            data.liabilities.longTerm.reduce((acc, i) => acc + parseCurrency(i.amount), 0);

        // Adjust Equity (Saldo Laba Ditahan) to make it balance perfectly
        const neededEquity = totalA - totalLP - parseCurrency(data.equity.base[0].amount);
        data.equity.base[1].amount = formatCurrency(neededEquity);
    }

    return data;
}

export function getUnitPerformanceData(unit: SchoolUnit) {
    const baseType = unit.split('-')[0];
    const unitName = getSchoolName(unit).replace('AL-AZHAR ', '');
    const schoolData = getSchoolData(unit);

    // Dynamic Capacity based on Unit
    let unitCapacity = 500;
    if (unit.includes('pekanbaru')) unitCapacity = 800;
    if (unit.includes('kampar')) unitCapacity = 450;
    if (unit.includes('siak')) unitCapacity = 350;
    if (unit.includes('sorek')) unitCapacity = 300;

    // 1. UNIT PERFORMANCE (SCHOOL LEVEL)
    const schoolPerf: UnitPerformance = {
        capacity: unitCapacity,
        kpis: [
            { label: 'Total Siswa Aktif', value: '452', change: '+12', trend: 'up', icon: GraduationCap },
            { label: 'Rata-rata Kehadiran', value: '94.8%', change: '+2.1%', trend: 'up', icon: Clock },
            { label: 'Skor Akreditasi', value: 'A (96)', change: 'Stable', trend: 'up', icon: Award },
            { label: 'Kelengkapan Data', value: '98%', change: '+5%', trend: 'up', icon: CheckCircle2 },
        ],
        studentTrend: [
            { month: 'Jul', total: 420, attendance: 92, capacity: unitCapacity },
            { month: 'Agu', total: 425, attendance: 94, capacity: unitCapacity },
            { month: 'Sep', total: 430, attendance: 93, capacity: unitCapacity },
            { month: 'Okt', total: 440, attendance: 95, capacity: unitCapacity },
            { month: 'Nov', total: 445, attendance: 96, capacity: unitCapacity },
            { month: 'Des', total: 452, attendance: 94, capacity: unitCapacity },
        ],
        insights: [
            `Tingkat kehadiran di ${unitName} meningkat signifikan di bulan November.`,
            'Rasio guru dan siswa berada dalam batas ideal (1:15).',
            'Perlu perhatian pada pemeliharaan sarana laboratorium di gedung B.'
        ]
    };

    // 2. GURU & STAFF PERFORMANCE
    const rawStaff = ((schoolData as any)[`${baseType}Staff`] || []) as Staff[];
    const staffPerf: StaffPerformance[] = rawStaff.map((s, i) => ({
        ...s,
        performanceScore: 85 + (i % 15),
        attendanceRate: `${92 + (i % 8)}%`,
        tasksCompleted: 10 + (i % 5),
        lastEvaluation: 'Januari 2026',
        photo: `https://api.dicebear.com/7.x/avataaars/svg?seed=${s.id}`
    }));

    // 3. SISWA PERFORMANCE
    const rawStudents = ((schoolData as any)[`${baseType}Students`] || []) as Student[];
    const studentPerf: StudentPerformance[] = rawStudents.map((s, i) => ({
        ...s,
        academicScore: 75 + (i % 25),
        attendanceRate: `${85 + (i % 15)}%`,
        behavior: (i % 4 === 0) ? 'Excelent' : (i % 4 === 1) ? 'Good' : 'Fair'
    }));

    // Variance Cases for different units
    if (unit.includes('pekanbaru')) {
        schoolPerf.kpis[0].value = '720';
        schoolPerf.kpis[1].value = '96.5%';
        schoolPerf.studentTrend = schoolPerf.studentTrend.map(t => ({ ...t, total: t.total + 250 }));
        schoolPerf.insights.push('Unit Pekanbaru menunjukkan pertumbuhan pendaftar tertinggi (+15%).');
    } else if (unit.includes('kampar')) {
        schoolPerf.kpis[0].value = '380';
        schoolPerf.kpis[1].value = '88.5%';
        schoolPerf.kpis[1].trend = 'down';
        schoolPerf.studentTrend = schoolPerf.studentTrend.map(t => ({ ...t, total: t.total - 70 }));
        schoolPerf.insights.push('Tingkat kehadiran di Kampar sedang dievaluasi terkait kendala transportasi.');
    } else if (unit.includes('siak')) {
        schoolPerf.kpis[0].value = '310';
        schoolPerf.studentTrend = schoolPerf.studentTrend.map(t => ({ ...t, total: t.total - 140 }));
        schoolPerf.insights.push('Siak hampir mencapai kapasitas maksimal (110% dari target optimal).');
    } else if (unit.includes('sorek')) {
        unitCapacity = 250;
        schoolPerf.capacity = unitCapacity;
        schoolPerf.kpis[0].value = '195';
        schoolPerf.studentTrend = schoolPerf.studentTrend.map(t => ({ ...t, total: t.total - 250, capacity: unitCapacity }));
    }

    return {
        school: schoolPerf,
        staff: staffPerf,
        students: studentPerf
    };
}

// Re-export all types and other data
export * from './yayasan';
export * from './types';
